import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

import Pane from 'components/Common/Pane'
import config from 'constants/config'

import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { OFFER_TYPE_VILLA_SLUG } from 'constants/offer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getCheapestSelectedPackageOptionWithDefault,
  areCalendarsLoading,
  getFromDurationLabel,
  HotelOfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import Caption from 'components/Luxkit/Typography/Caption'
import BodyText from 'components/Luxkit/Typography/BodyText'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import Group from 'components/utils/Group'
import { OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import WalledContent from 'components/Common/WalledContent'
import GenericLoginSignupWall from 'components/Common/WalledContent/GenericLoginSignupWall'
import OfferCalendarDatePickerSelect from '../Filters/CalendarPicker/OfferCalendarDatePickerSelect'
import { CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import OfferDurationSelect from '../Filters/Duration/OfferDurationSelect'
import TAOCancellationPolicies from 'components/Common/CancellationPolicies/AlwaysOn/TAOCancellationPolicies'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import OfferBookButton from '../OfferBookButton'
import Backdrop from 'components/utils/Backdrop'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import VillasPriceDetails from './VillasPriceDetails'
import OfferCapacityWithAgesVerticalSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesVerticalSelect'

const SummaryPane = styled(Pane)`
  padding: ${rem(24)};
  width: 100%;
  box-shadow: ${props => props.theme.shadow.bottom.medium};
`

const TwoColumnSelects = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(8)};

  > * {
    overflow: hidden;
  }
`

const CalendarSubFilters: Array<OfferPageFilterTypes> = ['capacity', 'duration']

interface Props {
  className?: string;
  offer: App.VillaOffer;
  rooms: Array<App.Occupants>;
  packageView: HotelOfferPackageOptionView;
  calendarsLoading?: boolean;
  durationLabel: string;
  checkIn?: moment.Moment;
  duration?: number;
  activeFilter?: OfferPageFilterTypes;
}

function VillasSummaryPanel(props: Props) {
  const {
    className,
    offer,
    packageView,
    rooms,
    duration,
    durationLabel,
    checkIn,
    activeFilter,
  } = props

  const {
    package: pkg,
    hotelPrice,
    propertyFees = 0,
    taxesAndFees,
  } = packageView

  const availableForPurchase = useIsAvailableForPurchase()

  const showPrices = packageView?.capacityValid && packageView?.hotelAvailable
  const showIncludedGuestsExceeded = useMemo(
    () =>
      !packageView?.package.roomRate.extraGuestSurcharges.length &&
      includedGuestsExceededItems(rooms || [], packageView?.package, offer),
    [packageView, rooms, offer],
  )

  const showPriceDetails = taxesAndFees > 0 || propertyFees > 0

  const canPurchase = availableForPurchase && packageView.capacityValid && packageView.hotelAvailable
  const canBuyNow = !!checkIn && !!rooms && canPurchase

  const filterContainer = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const parent = calendarRef.current?.closest<HTMLDivElement>('.sticky-container')
    if (activeFilter === 'capacity' || activeFilter === 'duration') {
      if (filterContainer.current && parent) {
        parent.style.top = `${-filterContainer.current.offsetTop + 12}px`
        window.setTimeout(() => filterContainer.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 250)
      }
    } else if (activeFilter === 'checkin') {
      if (calendarRef.current && parent) {
        parent.style.top = `${-calendarRef.current.offsetTop + 12}px`

        window.setTimeout(() => calendarRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 250)
      }
    }
    if (!activeFilter && parent) {
      parent.style.top = ''
    }
  }, [activeFilter])

  const capacities = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate.capacities)
  }, [offer])

  return (
    <SummaryPane
      bordered
      className={className}
      data-testid="SummaryPane"
    >
      <Backdrop visible={!!activeFilter} portalless/>
      <WalledContent enforced={offer.walledGarden} wall={<GenericLoginSignupWall />}>
        {availableForPurchase && (
          <VerticalSpacer gap={20}>
            {showPrices && (
              <VerticalSpacer gap={20}>
                <div>
                  <Caption variant="large" colour="neutral-two">
                    {durationLabel.toLowerCase()}
                  </Caption>
                  <VillasPriceDetails />
                  <Group direction="horizontal" gap={4}>
                    <TaxesAndFees
                      taxesPayableAtProperty={offer.property.taxesPayableAtProperty}
                      taxAmount={taxesAndFees}
                    />
                    {showPriceDetails && (
                      <PriceDetailsModal
                        offerPage
                        offer={offer}
                        duration={duration!}
                        taxesAndFees={taxesAndFees}
                        propertyFees={propertyFees}
                        hotelPrice={hotelPrice}
                        rooms={rooms}
                        extraGuestMessage={
                          showIncludedGuestsExceeded ?
                            pkg.roomPolicyDescription :
                            ''
                        }
                        dueAtPropertyMessage={offer.property.taxesAndFeesContent ?? ''}
                        label="infoButton"
                        labelSize="small"
                        cta={
                          <TextButton kind="primary" fit="flex">
                            View {config.brandConfig.travelSynonym} options
                          </TextButton>
                        }
                      />
                    )}
                  </Group>
                </div>
                <TAOCancellationPolicies
                  offerId={offer.id}
                  useDynamicCancellationPolicies={offer.property.useDynamicCancellationPolicies}
                  timezoneOffset={offer.property.timezoneOffset}
                  uniqueKey={pkg.uniqueKey}
                  checkInDate={checkIn ? checkIn.format(ISO_DATE_FORMAT) : undefined}
                  checkOutDate={checkIn ? checkIn.clone().add(duration, 'days').format(ISO_DATE_FORMAT) : undefined}
                  occupants={rooms}
                  cancellationPolicy={pkg.roomRate.cancellationPolicy}
                />
              </VerticalSpacer>
            )}
            <Group direction="vertical" gap={8}>
              <VerticalSpacer gap={8}>
                <TwoColumnSelects ref={filterContainer}>
                  <OfferCapacityWithAgesVerticalSelect
                    dropdownPosition="bottom-end"
                    anchorRef={filterContainer}
                    capacities={capacities}
                    disableMultiRoom
                  />
                  <OfferDurationSelect
                    dropdownPosition="bottom-end"
                    anchorRef={filterContainer}
                  />
                </TwoColumnSelects>
                <div ref={calendarRef}>
                  <OfferCalendarDatePickerSelect
                    onClickLabel={CALENDAR_GA_EVENT_LABEL.OfferIntro}
                    subFilters={CalendarSubFilters}
                    dropdownPosition="bottom-end"
                  />
                </div>
              </VerticalSpacer>
              <OfferBookButton
                type="submit"
                pkg={packageView.package}
              >
                {canBuyNow ? 'Book now' : 'Check availability'}
              </OfferBookButton>
            </Group>
          </VerticalSpacer>
        )}
        {!availableForPurchase && (
          <Group fullWidth direction="vertical" gap={20}>
            <BodyText variant="large" weight="bold" data-testid="OfferSoldOut">
              This villa is unavailable
            </BodyText>
            <TextButton
              kind="primary"
              to={`/${OFFER_TYPE_VILLA_SLUG}`}
              fit="flex"
            >
              Browse all villas
            </TextButton>
          </Group>
        )}
      </WalledContent>
    </SummaryPane>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    packageView: getCheapestSelectedPackageOptionWithDefault(appState, state),
    rooms: state.rooms,
    offer: state.offer,
    checkIn: state.checkInDate,
    calendarsLoading: areCalendarsLoading(appState, state),
    duration: state.duration,
    durationLabel: getFromDurationLabel(state, appState),
    activeFilter: state.activeFilter,
  }
})(VillasSummaryPanel)
